<template>
  <div>
    <v-navigation-drawer
      :key="forceRender"
      app
      :clipped="$vuetify.breakpoint.lgAndUp"
      v-model="$store.state.menu.opened"
    >
      <template v-if="$vuetify.breakpoint.mdAndDown">
        <div class="d-flex align-center my-5">
          <img
            class="mx-auto"
            src="@/assets/logo-dark-mobile.svg"
            :key="1"
            alt
            v-if="$vuetify.theme.dark"
          />
          <img
            class="mx-auto"
            src="@/assets/logo-day-mobile.svg"
            :key="2"
            alt
            v-else
          />
        </div>
        <v-divider></v-divider>
      </template>

      <v-list dense nav>
        <component
          :item="item"
          :data-test-menu="item.id"
          :is="component(item.type)"
          :key="index"
          v-on="$listeners"
          v-for="(item, index) in filteredMenu"
          @click="menuHandler"
          @action="handleAction"
        />
      </v-list>
    </v-navigation-drawer>
    <popup2
      :value="!!showPopup"
      @input="
        showPopup = null;
        action = null;
      "
      width="428"
    >
      <component
        v-if="showPopup"
        :show="showPopup"
        :is="showPopup"
        @close="showPopup = null"
      />
    </popup2>
  </div>
</template>

<script>
import DefaultMenuItem from './items/Default';
import DividerMenuItem from './items/Divider';
import NestedMenuItem from './items/Nested';
import Async from './items/Async';
import useUsers from '@/api/users';
import useGroups from '@/api/groups';
import { ref, computed, onMounted } from '@vue/composition-api';
import useAuth from '@/api/auth';
import { watch } from '@vue/composition-api';
import { nanoid } from 'nanoid';

export default {
  name: 'AppNavigationDrawer',
  components: {
    DefaultMenuItem,
    DividerMenuItem,
    NestedMenuItem,
    Async,
    about: () => import('./about'),
    menuUserPopup: () => import('./MenuUserPopup')
  },
  setup(props, { root }) {
    const { groups: userGroupsApi } = new useUsers();
    const { list: userGroups } = userGroupsApi;
    const { profile, loadProfileAsync } = useAuth();

    const { groups: allGroupsApi } = new useGroups({ type: 'ASSET' });
    const { list: allGroups } = userGroupsApi;

    const action = ref(null);
    const forceRender = ref(`id_${nanoid()}`);
    const showPopup = ref(null);
    const menuItems = ref([...root.$store.state.menu.menuItems]);

    const filteredMenu = computed(() => {
      const standardItemsProp = profile.value?.objectProperties.find(
        item => item.key === 'menuSettingsStandardItems'
      ) || { value: [] };

      const customItemsProp = profile.value?.objectProperties.find(
        item => item.key === 'menuSettingsCustomItems'
      ) || { value: [] };

      const standardItems = menuItems.value
        .filter(item => {
          if (!root.$vuetify.breakpoint.xsOnly) {
            return !item.hideOnDesktop;
          }
          return true;
        })
        .filter(
          item =>
            standardItemsProp.value.includes(item.id) ||
            item.ignore ||
            item.id === 'divider'
        );

      if (customItemsProp.value.length) {
        return [
          ...customItemsProp.value.map((item, index) => ({
            id: `${item.name}_${index}`,
            title: item.name,
            icon: 'mdi-bookmark-box-multiple',
            to: `/collections/${item.id}`,
            type: 'default'
          })),
          {
            id: 'divider',
            type: 'divider',
            hideOnDesktop: true
          },
          ...standardItems
        ];
      }
      return standardItems;
    });

    function component(type) {
      switch (type) {
        case 'divider':
          return 'DividerMenuItem';
        case 'group':
          return 'NestedMenuItem';
        case 'async':
          return 'Async';
        default:
          return 'DefaultMenuItem';
      }
    }
    function menuHandler(item) {
      if (item.id === 'help') {
        window.open(item.link, '_blank');
      }
      if (item.component) {
        showPopup.value = item.component;
        action.value = item.action;
      } else if (item.action) {
        switch (item.action) {
          case 'addAppGroup':
            break;
        }
      }
    }

    function handleAction({ action, id, open }) {
      switch (action) {
        case 'openUsersGroups': {
          const item = menuItems.value.find(i => i.id === id);
          if (open) {
            userGroupsApi.load().then(() => {
              item.children.unshift(
                ...userGroups.value.map(i => ({
                  id: i.id,
                  title: i.name,
                  to: { name: 'users', query: { group: i.id } },
                  actions: [
                    {
                      id: 'edit-group',
                      icon: 'mdi-pencil'
                    },
                    {
                      id: 'delete-group',
                      icon: 'mdi-delete'
                    }
                  ]
                }))
              );
            });
          } else {
            item.children = item.children.filter(
              i => i.id === 'add-user-group'
            );
          }
          break;
        }
        case 'openAllGroups': {
          const item = menuItems.value.find(i => i.id === id);
          if (open) {
            allGroupsApi.load().then(() => {
              item.children.unshift(
                ...allGroups.value.map(i => ({
                  id: i.id,
                  title: i.name,
                  actions: [
                    {
                      id: 'edit-group',
                      icon: 'mdi-pencil'
                    },
                    {
                      id: 'delete-group',
                      icon: 'mdi-delete'
                    }
                  ]
                }))
              );
            });
          } else {
            item.children = item.children.filter(i => i.id === 'add-all-group');
          }
          break;
        }
      }
      // handle menu action
    }

    function updateGroups(data) {
      const result = {
        id: data.id,
        title: data.name,
        actions: [
          {
            id: 'edit-group',
            icon: 'mdi-pencil'
          },
          {
            id: 'delete-group',
            icon: 'mdi-delete'
          }
        ]
      };
      switch (action.value) {
        case 'addUserGroup': {
          const temp = menuItems.value.find(i => i.id === 'usergroups');
          temp.children.unshift(result);
          break;
        }
        case 'addAppGroup': {
          const temp = menuItems.value.find(i => i.id === 'appgroups');
          temp.children.unshift(result);
          break;
        }
        case 'addAllGroup': {
          const temp = menuItems.value.find(i => i.id === 'allgroups');
          temp.children.unshift(result);
          break;
        }
      }
      action.value = null;
      showPopup.value = null;
    }

    onMounted(() => {
      loadProfileAsync();
    });

    return {
      component,
      filteredMenu,
      showPopup,
      menuHandler,
      handleAction,
      action,
      updateGroups,
      forceRender
    };
  }
};
</script>

<style lang="sass">
.menu-item
    font-size: 14px !important
</style>
